<script lang="ts" setup>
import { computed, ref } from "vue";
import BaseChart, {
  type ChartData,
  type ChartOptions,
} from "@/views/shared/components/BaseChart.vue";
import DashboardHeader from "@/components/DashboardHeader.vue";
import { useDashboardSummary } from "@/queries/dashboard";
import { STATUS_COLORS } from "./chartPalette";

// Failed + warning runs grouped by whatever dimension fits the org — the backend picks product,
// project, or workflow (groupDimension) so the panel reads sensibly for publishers, subscribers,
// and DIY orgs alike. Horizontal stacked bar, ordered by most-troubled group first.
const range = ref("auto");
const { summary, summaryLoading, summaryError } = useDashboardSummary(range);

// Show every group, including healthy (zero-error) ones like Peach — a missing bar is ambiguous
// (no data? filtered? healthy?), whereas a zero-height bar with its label clearly reads as "no
// errors". The backend sorts most-troubled first and we cap at 12, so a handful of product/project
// groups all stay visible while a long workflow list drops its healthy tail off the cut.
const groups = computed(() => summary.value.groups.slice(0, 12));
// Only celebrate the empty state when the *whole* org is clean, not when one group happens to be.
const totalIssues = computed(() =>
  summary.value.groups.reduce((sum, g) => sum + g.failed + g.warning, 0)
);
const hasData = computed(() => totalIssues.value > 0);

const dimensionLabel = computed(() => {
  switch (summary.value.groupDimension) {
    case "product":
      return "by product";
    case "project":
      return "by project";
    default:
      return "by workflow";
  }
});

const chartData = computed<ChartData>(() => ({
  labels: groups.value.map((g) => g.label),
  datasets: [
    {
      label: "Failed",
      data: groups.value.map((g) => g.failed),
      backgroundColor: STATUS_COLORS.failed.bg,
      borderColor: STATUS_COLORS.failed.border,
      borderWidth: 1,
    },
    {
      label: "Warning",
      data: groups.value.map((g) => g.warning),
      backgroundColor: STATUS_COLORS.warning.bg,
      borderColor: STATUS_COLORS.warning.border,
      borderWidth: 1,
    },
  ],
}));

const chartOptions = computed<ChartOptions>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: "y",
  plugins: {
    legend: { display: true, position: "top" },
    title: { display: false },
    tooltip: {
      callbacks: {
        title: (context: any) => context[0].label,
        label: (context: any) => `${context.dataset.label}: ${context.parsed.x}`,
      },
    },
  },
  scales: {
    x: { beginAtZero: true, stacked: true, grid: { display: true } },
    y: { stacked: true, grid: { display: false } },
  },
}));
</script>

<template>
  <DashboardHeader :title="'Workflow Errors'">
    <template #actions>
      <span v-if="hasData" class="text-muted small align-self-center">{{ dimensionLabel }}</span>
    </template>
  </DashboardHeader>

  <div class="col-xxl-12">
    <div class="row">
      <div class="col">
        <div style="height: 40vh; position: relative">
          <div class="my-4" style="height: 300px">
            <div v-if="summaryLoading && !hasData" class="text-muted py-5 text-center">
              Loading…
            </div>
            <div v-else-if="summaryError" class="text-danger py-5 text-center">
              Couldn't load workflow errors.
            </div>
            <div v-else-if="!hasData" class="text-muted py-5 text-center">
              No failures or warnings in this range. 🎉
            </div>
            <BaseChart
              v-else
              type="bar"
              :data="chartData"
              :options="chartOptions"
              :height="300"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss"></style>
